import React from 'react';
import { Button } from './ui/button';
import { Facebook, Instagram, Calendar } from 'lucide-react';

export const ChoirSection = () => {
  return (
    <section id="choir" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nadja Scott & Divine Anointing Choir
          </h2>
          <p className="text-2xl text-red-600 font-bold mb-6">
            Award-Winning, Spirit-Led, Fire-Filled
          </p>
          <div className="w-24 h-1 bg-red-600 mx-auto"></div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Introduction */}
          <p className="text-lg text-gray-300 leading-relaxed">
            Nadja Scott & Divine Anointing, widely known as <span className="font-semibold text-white">DA</span>, is an award-winning intergenerational gospel choir anointed to minister to both believers and seekers through powerful worship, musical excellence, and spiritual authenticity. Set apart for such a time as this, DA stands as a vessel of God's glory—commissioned to share the message of Jesus Christ with all who will listen. Rooted in Matthew 5:13, the choir embraces its calling to be the "salt" the world has been missing.
          </p>

          {/* Built on Prayer */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Built on Prayer. Sustained by Unity. Empowered by God.</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              Very early in their journey, DA recognized that true power in ministry comes only through prayer and consistent communion with God. In response, they launched the <span className="font-semibold text-white">Divine Anointing "Hour of Power" Prayer Line</span>, a weekly space of intercession, reflection, and spiritual alignment held every Thursday at 8 PM. This prayer foundation continues to fuel the choir's unity, endurance, and effectiveness in ministry.
            </p>
          </div>

          {/* Multigenerational */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">A Multigenerational, Nationwide Ministry</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              DA is a family that spans generations, with members ranging from age six to sixty and representing regions including Baltimore, Maryland; Washington, D.C.; Pennsylvania; North Carolina; South Carolina; and Florida. Each member brings a unique testimony, yet all move in one sound—empowered by God's wisdom, guided by His favor.
            </p>
          </div>

          {/* National Recognition */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Recognized on National Stages</h3>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              In a short time, Divine Anointing has shared platforms with top gospel influencers and national voices, including:
            </p>
            <ul className="text-lg text-gray-300 leading-relaxed space-y-2 ml-6">
              <li>• Prophetess Juanita Bynum</li>
              <li>• Professor James Hall</li>
              <li>• National Recording Artist Sonnie Badu</li>
              <li>• Minister David Hammond, and more</li>
            </ul>
            <p className="text-lg text-gray-300 leading-relaxed mt-4">
              One of DA's most celebrated milestones came when the choir won <span className="font-semibold text-white">Shirley Caesar's Battle of the Bands</span>, earning the opportunity to record alongside the legendary Pastor Shirley Caesar.
            </p>
          </div>

          {/* Ministry Beyond the Stage */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Ministry Beyond the Stage</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              While DA has graced major gospel stages, their greatest joy remains serving those in need. Some of their most transformative ministry moments occur within homeless shelters, outreach centers, and community spaces throughout Baltimore and surrounding areas, where they bring hope, healing, and joy through music and prayer.
            </p>
          </div>

          {/* Ministry of Sound */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">A Ministry of Sound, Power, and Purpose</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              Guided by visionary founder Nadja Scott — best-selling author, acclaimed playwright, award-winning vocalist, and worship leader — Divine Anointing continues to evolve into a movement that merges worship, storytelling, artistry, and service for the glory of God.
            </p>
          </div>
        </div>

        {/* Image Grid */}
        <div className="grid md:grid-cols-3 gap-6 my-16">
          <div className="overflow-hidden rounded-2xl shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_gospel-choir/artifacts/2gtozwqn_IMG_6155.JPG"
              alt="Divine Anointing Choir"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="overflow-hidden rounded-2xl shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_gospel-choir/artifacts/q5b9xq6v_IMG_9098.JPG"
              alt="Divine Anointing Choir Performance"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="overflow-hidden rounded-2xl shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_gospel-choir/artifacts/19326i60_IMG_5409.JPG"
              alt="Divine Anointing Choir Ensemble"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>

        {/* Stay Connected */}
        <div className="bg-gray-900 rounded-2xl p-8 border border-gray-700">
          <h3 className="text-3xl font-bold text-white text-center mb-8">
            Stay Connected with Nadja Scott & Divine Anointing Choir
          </h3>
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <a 
              href="https://facebook.com/DivineAnointingCommunityChoir"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-red-600 transition-colors"
            >
              <Facebook className="w-6 h-6" />
              <span className="text-lg">facebook.com/DivineAnointingCommunityChoir</span>
            </a>
            <span className="text-gray-600 hidden md:block">|</span>
            <a 
              href="https://instagram.com/iamnadjascottandda"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-red-600 transition-colors"
            >
              <Instagram className="w-6 h-6" />
              <span className="text-lg">@iamnadjascottandda</span>
            </a>
          </div>
          <div className="text-center mt-8">
            <a 
              href="https://docs.google.com/forms/d/12hphFMKBt-Zd44mV26mRSBOZkxx0veNAx_E3yN2YRw8/viewform"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full text-lg flex items-center gap-2 mx-auto">
                <Calendar className="w-5 h-5" />
                Bookings & Inquiries
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
